import React, { useMemo } from 'react'

import { useScript } from 'hoofd'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const Creation = () => {
  const howToData = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Create an Event on Guild',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Open the Command Palette',
            text: 'Click the Command Palette icon in the top navigation bar to open the Command Palette.',
          },
          {
            '@type': 'HowToStep',
            name: 'Select "Create an Event"',
            text: 'Click the "Create an Event" quick action, or type "create event" into the search input.',
          },
          {
            '@type': 'HowToStep',
            name: 'Choose the host',
            text: 'Select which Guild will host the Event. You must be an Organizer or Editor of the Guild.',
          },
          {
            '@type': 'HowToStep',
            name: 'Enter Event details',
            text: 'Enter the Event title, select start and end times, and choose a timezone.',
          },
          {
            '@type': 'HowToStep',
            name: 'Configure venue',
            text: 'Set up the venue with a physical address for in-person Events, a meeting link for online Events, or both for hybrid Events.',
          },
          {
            '@type': 'HowToStep',
            name: 'Add description and cover photo',
            text: 'Write a description and upload a cover photo for the Event.',
          },
          {
            '@type': 'HowToStep',
            name: 'Save and publish',
            text: 'Save your Event to publish it. Attendees can now RSVP or purchase tickets.',
          },
        ],
      }),
    []
  )
  useScript({ type: 'application/ld+json', text: howToData })

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          Events are created through the{' '}
          <Paragraph size='$5' fontWeight='$7'>Command Palette</Paragraph> — a
          search and action interface in the top navigation bar. Any{' '}
          <Link href='/docs/guilds/members'>Member</Link> with the Organizer or
          Editor role on a Guild can create an Event for that Guild.
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='creating-an-event-steps' title='Step-by-Step'>
          <DocStep
            number={1}
            title='Open the Command Palette'
            description='Click the Command Palette icon in the top navigation bar. A search input appears with quick action options below it.'
            screenshot={{
              src: 'docs/getting-started/command-menu-open.png',
              alt: 'Command Palette open showing search input and quick actions including Create an Event, Create a Presentation, and Create a Guild',
              caption:
                'The Command Palette with quick actions for creating Events, Presentations, and Guilds',
            }}
          />
          <DocStep
            number={2}
            title='Select "Create an Event"'
            description='Click the "Create an Event" quick action from the list, or type "create event" into the search input to find it.'
          />
          <DocStep
            number={3}
            title='Choose the host Guild'
            description='Select which Guild will host the Event. You must have the Organizer or Editor role on the Guild to create Events for it. If you belong to multiple Guilds, you can pick any one where you have the required role.'
          />
          <DocStep
            number={4}
            title='Enter Event details'
            description='Enter a title for your Event, then select start and end times. Choose the timezone that applies to your Event so attendees see the correct local time.'
          />
          <DocStep
            number={5}
            title='Configure the venue'
            description='For in-person Events, enter a venue address — attendees will see it as a clickable link to the map. For online Events, add a meeting link (e.g. Zoom, Google Meet). Enable both for hybrid Events that support in-person and virtual attendance.'
          />
          <DocStep
            number={6}
            title='Add a description and cover photo'
            description='Write a description that tells attendees what to expect. Upload a cover photo to make your Event stand out in listings and social shares.'
          />
          <DocStep
            number={7}
            title='Save and publish'
            description="Save your Event to publish it. It will appear on your Guild's Events tab and attendees can begin RSVPing or purchasing tickets. You can continue editing details from Event Settings at any time."
          />
        </DocSection>
      </Card>
    </>
  )
}

export default Creation
